import React, { useState, useEffect } from "react";
import { X, Instagram, Facebook, Linkedin, Phone, Globe, MapPin, Users, FileText } from "lucide-react";
import api from "@/utils/connector/ApiConnector";
import { useUser } from "@/utils/userContext/UserContext";
import { useGlobalModal } from "@/components/GlobalModalProvider/GlobalModalProvider";
import { documentHelper } from "@/utils/common/documentHelper";
import { phoneNumberHelper } from "@/utils/common/phoneNumberHelper";
import "./EditCoworkingModal.css";

interface CoworkingData {
  id: string;
  name: string;
  instagram?: string;
  facebook?: string;
  linkedin?: string;
  whatsapp?: string;
  phone?: string;
  website?: string;
  address?: string;
  capacity?: number;
  cnpj?: string;
}

interface EditCoworkingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  coworkingData?: CoworkingData | null;
}

// Constantes para valores iniciais
const INITIAL_FORM_STATE: Partial<CoworkingData> = {
  name: "",
  instagram: "",
  facebook: "",
  linkedin: "",
  whatsapp: "",
  phone: "",
  website: "",
  address: "",
  capacity: undefined,
  cnpj: "",
};

const mapCoworkingToFormData = (data: CoworkingData): Partial<CoworkingData> => ({
  name: data.name || "",
  instagram: data.instagram || "",
  facebook: data.facebook || "",
  linkedin: data.linkedin || "",
  whatsapp: data.whatsapp || "",
  phone: data.phone || "",
  website: data.website || "",
  address: data.address || "",
  capacity: data.capacity || undefined,
  cnpj: data.cnpj || "",
});

const EditCoworkingModal: React.FC<EditCoworkingModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  coworkingData,
}) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<Partial<CoworkingData>>(INITIAL_FORM_STATE);

  const { userData } = useUser();
  const modal = useGlobalModal();

  useEffect(() => {
    if (coworkingData) {
      setFormData(mapCoworkingToFormData(coworkingData));
    }
  }, [coworkingData]);

  if (!isOpen) return null;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "number" ? (value ? parseInt(value, 10) : undefined) : value,
    }));
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: phoneNumberHelper.formatPhone(value),
    }));
  };

  const handleCNPJChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      cnpj: documentHelper.formatCNPJ(e.target.value),
    }));
  };

  const validateForm = (): boolean => {
    if (!formData.name?.trim()) {
      modal?.show({
        type: "warning",
        title: "Campo obrigatório",
        message: "O nome do coworking é obrigatório.",
        autoClose: 3000,
      });
      return false;
    }
    return true;
  };

  const buildPayload = () => ({
    name: formData.name,
    instagram: formData.instagram || null,
    facebook: formData.facebook || null,
    linkedin: formData.linkedin || null,
    whatsapp: formData.whatsapp || null,
    phone: formData.phone || null,
    website: formData.website || null,
    address: formData.address || null,
    capacity: formData.capacity || null,
    cnpj: formData.cnpj || null,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);

    try {
      await api.patch("/coworking/my", buildPayload(), {
        headers: {
          Authorization: `Bearer ${userData?.auth}`,
          "Content-Type": "application/json",
        },
      });

      modal?.show({
        type: "success",
        title: "Sucesso",
        message: "Informações do coworking atualizadas com sucesso!",
        autoClose: 3000,
      });

      onSuccess?.();
      onClose();
    } catch (err: any) {
      console.error("Erro ao atualizar coworking:", err);
      modal?.show({
        type: "error",
        title: "Erro",
        message: err?.response?.data?.message || "Erro ao atualizar informações. Tente novamente.",
        autoClose: 4000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="edit-coworking-modal-overlay">
      <div className="edit-coworking-modal-content">
        <div className="edit-coworking-modal-header">
          <h2>Editar Informações do Coworking</h2>
          <button className="close-button" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="edit-coworking-form">
          <div className="form-section">
            <h3 className="section-title">Informações Básicas</h3>
            
            <div className="form-group">
              <label htmlFor="name">Nome do Espaço *</label>
              <input
                id="name"
                name="name"
                type="text"
                value={formData.name}
                onChange={handleChange}
                placeholder="Nome do coworking"
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="cnpj">
                  <FileText size={14} />
                  CNPJ
                </label>
                <input
                  id="cnpj"
                  name="cnpj"
                  type="text"
                  value={formData.cnpj}
                  onChange={handleCNPJChange}
                  placeholder="00.000.000/0000-00"
                  maxLength={18}
                />
              </div>

              <div className="form-group">
                <label htmlFor="capacity">
                  <Users size={14} />
                  Capacidade
                </label>
                <input
                  id="capacity"
                  name="capacity"
                  type="number"
                  value={formData.capacity ?? ""}
                  onChange={handleChange}
                  placeholder="Ex: 150"
                  min={0}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="address">
                <MapPin size={14} />
                Endereço
              </label>
              <textarea
                id="address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="Rua, número, bairro, cidade - UF"
                rows={2}
              />
            </div>
          </div>

          <div className="form-section">
            <h3 className="section-title">Contato</h3>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="whatsapp">
                  <Phone size={14} />
                  WhatsApp
                </label>
                <input
                  id="whatsapp"
                  name="whatsapp"
                  type="text"
                  value={formData.whatsapp}
                  onChange={handlePhoneChange}
                  placeholder="(00) 00000-0000"
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone">
                  <Phone size={14} />
                  Telefone
                </label>
                <input
                  id="phone"
                  name="phone"
                  type="text"
                  value={formData.phone}
                  onChange={handlePhoneChange}
                  placeholder="(00) 0000-0000"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="website">
                <Globe size={14} />
                Website
              </label>
              <input
                id="website"
                name="website"
                type="url"
                value={formData.website}
                onChange={handleChange}
                placeholder="https://www.seusite.com.br"
              />
            </div>
          </div>

          <div className="form-section">
            <h3 className="section-title">Redes Sociais</h3>
            
            <div className="form-group">
              <label htmlFor="instagram">
                <Instagram size={14} />
                Instagram
              </label>
              <input
                id="instagram"
                name="instagram"
                type="url"
                value={formData.instagram}
                onChange={handleChange}
                placeholder="https://instagram.com/seucoworking"
              />
            </div>

            <div className="form-group">
              <label htmlFor="facebook">
                <Facebook size={14} />
                Facebook
              </label>
              <input
                id="facebook"
                name="facebook"
                type="url"
                value={formData.facebook}
                onChange={handleChange}
                placeholder="https://facebook.com/seucoworking"
              />
            </div>

            <div className="form-group">
              <label htmlFor="linkedin">
                <Linkedin size={14} />
                LinkedIn
              </label>
              <input
                id="linkedin"
                name="linkedin"
                type="url"
                value={formData.linkedin}
                onChange={handleChange}
                placeholder="https://linkedin.com/company/seucoworking"
              />
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="cancel-button" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="submit-button" disabled={loading}>
              {loading ? "Salvando..." : "Salvar Alterações"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditCoworkingModal;

